<template>
<div class="container">
  <page-title title="Pay-In Request"/>
  <hr class="border border-dark"/>
  
  <div v-if="view === 'form'" class="row mb-2">
    <input-select v-model="channel" group-class="col-auto" label="Channel">
      <option v-for="(ch, idx) in channels" :key="idx" :value="ch">{{ch.channelCode}} -  {{ch.description}}</option>
    </input-select>
  </div>

  <form v-if="channel && view === 'form'">
    <fieldset :disabled="formDisabled">
    <div class="row mb-3">
      <input-number v-model="amount" label="Amount" :append="channel.currency" group-class="col-auto" input-class="fw-bold"></input-number>
      <input-text v-model="paymentRef" label="Payment Reference" group-class="col-6 ms-auto"></input-text>
    </div>
  
    <div class="border px-2 pb-3 mb-3">
      <div class="row bg-dark">
        <div class="col-auto fst-italic fw-bold small text-info py-2">EndUser Info</div>
      </div>
      <div class="row mb-2">
        <input-text v-model="userInfo.firstName" label="First Name" group-class="col-6"></input-text>
        <input-text v-model="userInfo.lastName" label="Last Name" group-class="col-6"></input-text>
      </div>
      <hr class="my-3 border border-3 border-dark"/>
      <div class="row mb-2">
        <div class="col-6">
          <input-text v-if="isUserInfoRequired('emailAddress')" v-model="userInfo.emailAddress" label="Email Address"></input-text>
          <input-text v-if="isUserInfoRequired('phoneNumber')" v-model="userInfo.phoneNumber" label="Phone Number"></input-text>
          <input-text v-if="isUserInfoRequired('dob')" v-model="userInfo.dob" label="Date of Birth (yyyyMMdd)"></input-text>
          <input-text v-if="isUserInfoRequired('identifier')" v-model="userInfo.identifier" label="User Identifier"></input-text>
          <input-text v-if="isUserInfoRequired('ipAddress')" v-model="userInfo.ipAddress" label="IP Address"></input-text>
        </div>
        <div class="col-6" v-if="isUserInfoRequired('address')">
        <input-text v-model="userInfo.address.addressLine1" label="Address Line 1"></input-text>
        <input-text v-model="userInfo.address.addressLine2" label="Address Line 2"></input-text>
        <input-text v-model="userInfo.address.cityTown" label="City/Town"></input-text>
        <input-text v-model="userInfo.address.stateProv" label="State/Province"></input-text>
        <input-select v-model="userInfo.address.country" label="Country">
          <option v-for="c in countries" :key="c.code" :value="c.code">{{c.name}}</option>
        </input-select>
        <input-text v-model="userInfo.address.postalCode" label="Postal Code"></input-text>
        </div>
      </div>
    </div>
    
    <div class="border px-2 pb-3 mb-3">
      <div class="row bg-dark mb-3">
        <div class="col-auto fst-italic fw-bold small text-info py-2">Payment Info</div>
      </div>
      <div class="row" v-if="specs && specs.requiredPayInfo && specs.requiredPayInfo.length > 0">
        <div class="row mb-1" v-for="(fld, idx) in specs.requiredPayInfo" :key="idx">
          <div class="col-md-3 col-sm-6 mt-2 ms-auto me-0 pe-0 fw-bold"><span class="fill-line">{{fld.description}}</span></div>
          <input-text v-model="payInfo[fld.fieldName]" group-class="col-md-4 col-sm-6 me-auto"></input-text>
        </div>
      </div>
      <div class="row" v-else>
        <div class="col-auto mx-auto fw-bold fs-6 my-3 text-info">This channel does not require PayInfo fields for PAY-IN</div>
      </div>
    </div>

    <div class="row mb-5">
      <div v-if="formDisabled" class="col text-center fw-bold text-warning bg-secondary py-3 mx-3">
        <b-spinner small variant='warning' label='Please wait'></b-spinner>
        <span class="ms-3">Processing ... Please wait</span>
      </div>
      <div v-else class="col text-center">
        <button class="btn btn-secondary mx-2" @click="reinitialize()">Cancel</button>
        <button class="btn btn-primary mx-2" @click.prevent="onSubmit()">Submit</button>
      </div>
    </div>
    </fieldset>
  </form>
  
  <div v-if="view === 'result' && payinResult">
    <div class="row border py-2 mb-5 bg-light">
      <div v-if="payinResult.status === 'FORAUTHZ' && payinResult.authzUrl" class="col text-center">
        <p class="text-info fw-bold">Please copy and send the following link to the customer to fulfill this request:</p>
        <p class="text-warning fw-bold">
          <span>{{payinResult.authzUrl}}</span>
          <span v-if="isCopied" class="small text-info mx-2">Copied!</span>
          <a v-else href="#" @click.prevent="onCopyToClipboard(payinResult.authzUrl)" v-b-tooltip.hover title="Copy to Clipboard"><i class="far fa-copy mx-2 text-info"></i></a>        
        </p>
      </div>
      <div v-else class="col text-center">
        <p class="text-info fw-bold"> 
          <span class="fs-5" :class="getStatusClass(payinResult.status)">{{payinResult.status}}</span>
        </p>
      </div>
      <div class="row mt-3">
        <div class="col text-end">
          <button class="btn btn-outline-info mx-2 fst-italic" @click="reinitialize()">Create Another Request</button>
          <button class="btn btn-outline-info mx-2 fst-italic" @click="$router.push('/transactions')">Go to Transaction List</button>
        </div>
      </div>
    </div>
    <txn-details :txn-id="payinResult.txnId"/>
  </div>
</div>
</template>

<script>
import HelpersMixin from '../utils/HelpersMixin.js'
import TxnDetails from '../components/TxnDetails.vue'
import { countriesList } from '../utils/Countries.js'
import { copyToClipboard } from '../utils/clipboard.js'
export default {
  name: 'PayinTerminal',
  mixins: [HelpersMixin],
  components: { TxnDetails },
  data () {
    return {
      channels: [],
      channel: null,
      amount: 0,
      paymentRef: '',
      userInfo: {
          identifier: '', 
          firstName: '', 
          lastName: '', 
          phoneNumber: '', 
          emailAddress: '', 
          dob: '',
          ipAddress: '', 
          address: {addressLine1: '', addressLine2: '', cityTown: '', stateProv: '', country: '', postalCode: ''} 
      },
      payInfo: {},
      countries: countriesList,
      isCopied: false,
      formDisabled: false,
      payinResult: {},
      view: 'form'
    }
  },
  computed: {
    specs: function() {
      if (this.channel && this.channel.payinSpecs) {
        return this.channel.payinSpecs
      } else {
        return null
      }
    }
  },
  mounted () {
    this.getPayinChannels()
  },
  methods: {
    getPayinChannels: function () {
      this.axios.get('/mx/payinchannels')
      .then (response => {
        this.channels = response.data
      })
      .catch(error => this.handleError(error))
    },
    onSubmit: function () {
      this.formDisabled = true
      this.payinResult = {}
      var payinRequest = {
        channelCode: this.channel.channelCode,
        txnAmount: this.amount,
        txnCurrency: this.channel.currency,
        paymentRef: this.paymentRef,
        endUserInfo: this.userInfo,
        payInfo: this.payInfo
      }
      this.axios.post('/mx/payinrequest', payinRequest) 
      .then(response => {
        this.payinResult = response.data
        this.view = "result"
      })
      .catch(error => { 
        this.handleError(error)
        this.formDisabled = false
      })
    },
    isUserInfoRequired: function (field) {
      if (!this.specs || !this.specs.requiredEndUserInfo) {
        return false
      }
      const isRequired = this.specs.requiredEndUserInfo.some(item => item.fieldName === field)
      return isRequired
    },
    onCopyToClipboard: async function (textToCopy) {
      await copyToClipboard(textToCopy, {
        timeout: 2500,
        onStatusChange: (status) => {
          this.isCopied = status;
        }
      });
           
    },
    getStatusClass: function (status) {
      if (status === 'COMPLETED') {
        return 'text-success'
      } else if (status === 'FAILED') {
        return 'text-danger'
      } else {
        return 'text-warning'
      }
    },
    reinitialize: function () {
      Object.assign(this.userInfo, this.$options.data.call(this).userInfo)
      this.channel = null
      this.amount = 0
      this.paymentRef = ''
      this.payInfo = {}
      this.view = 'form'
    }
   }
}
</script>

<style scoped>
.fill-line {
  display: flex;
  align-items: baseline;
  width: 100%;
  white-space: nowrap;
}

.fill-line::after {
  content: " . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . ";
  flex-grow: 1;
  overflow: hidden;
  text-overflow: clip;
  padding-left: 5px; /* Spacing between text and characters */
}

</style>